/**
 * HTTP Client with Token Refresh Interceptors
 *
 * WHY NOT JUST USE fetch():
 * React-Admin components fire many parallel API requests. Each needs:
 *   1. A valid JWT attached before sending (request interceptor)
 *   2. Automatic retry on 401 with a fresh token (response interceptor)
 *   3. Coordination so only ONE refresh happens when multiple requests
 *      detect expiration simultaneously (delegated to tokenRefreshService)
 *
 * Plain fetch() would require every call site to handle token attachment,
 * expiration detection, refresh coordination, and retry logic manually.
 * Axios interceptors centralize this in one place.
 *
 * WHY tokenRefreshService IS THE SINGLE COORDINATION POINT:
 * Previously this file had its OWN subscriber queue alongside tokenRefreshService's
 * queue. Two independent queues meant a refresh triggered here wouldn't notify
 * waiters registered in tokenRefreshService (and vice versa). Now ALL refresh
 * coordination — singleton gating, subscriber notification, token persistence —
 * flows through tokenRefreshService.refreshToken(). This file only calls it.
 *
 * INTERCEPTOR FLOW (for a single request):
 *   Request interceptor:
 *     1. If refresh in progress → waitForRefresh() → use fresh token
 *     2. Else if token expiring soon → refreshToken() → use fresh token
 *     3. Else → use current token
 *   Response interceptor (fallback for clock skew / server-side revocation):
 *     4. On 401 → refreshToken() → retry original request with fresh token
 *     5. If refresh fails → reject promise → React-Admin checkError handles logout
 */

import axios, { AxiosInstance, AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import * as Sentry from '@sentry/react';
import Logger from '../utils/Logger';
import config from '../config';
import {
  getStoredToken,
  shouldRefreshToken,
  refreshToken,
  isCurrentlyRefreshing,
  waitForRefresh,
  getValidToken,
} from './tokenRefreshService';

// Create axios instance for authenticated requests
const authInstance: AxiosInstance = axios.create({
  baseURL: config.API_URL,
  timeout: 30000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

// Create axios instance for public requests (no auth needed)
const publicInstance: AxiosInstance = axios.create({
  baseURL: config.API_URL,
  timeout: 30000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

/**
 * Request Interceptor — Preemptive Token Refresh
 *
 * WHY PREEMPTIVE:
 * Waiting for a 401 response adds a full round-trip of latency. By checking
 * token expiration BEFORE sending, we avoid the failed request entirely.
 * The 5-minute buffer (TOKEN_REFRESH_BUFFER_SECONDS in tokenRefreshService)
 * accounts for clock skew between client and server.
 *
 * WHY waitForRefresh() INSTEAD OF refreshToken():
 * If another interceptor or authProvider already started a refresh, calling
 * refreshToken() again would be a no-op (singleton gate), but waitForRefresh()
 * is semantically clearer: "I know a refresh is happening, just give me the
 * result when it's done."
 */
authInstance.interceptors.request.use(
  async (requestConfig: InternalAxiosRequestConfig) => {
    // Track request start time for latency metrics
    (requestConfig as any)._startTime = Date.now();

    // The instance default is Content-Type: application/json, and axios 1.x
    // JSON-serialises a FormData body when it sees that header — every File
    // becomes {} and the API rejects the upload. Drop it so the browser sets
    // multipart/form-data with its boundary.
    if (requestConfig.data instanceof FormData && requestConfig.headers) {
      requestConfig.headers.delete('Content-Type');
    }

    // If currently refreshing, wait for it via tokenRefreshService (single coordination point)
    if (isCurrentlyRefreshing()) {
      const freshToken = await waitForRefresh();
      if (freshToken && requestConfig.headers) {
        requestConfig.headers.Authorization = `Bearer ${freshToken}`;
      }
      return requestConfig;
    }

    let token = getStoredToken();

    if (token) {
      // Check if token needs preemptive refresh
      if (shouldRefreshToken(token)) {
        Logger.info('[HttpClient] Token expiring soon, refreshing preemptively...');
        const newToken = await refreshToken();
        if (newToken) {
          token = newToken;
        } else {
          // Refresh failed - will redirect to login via response interceptor
          Logger.warn('[HttpClient] Preemptive token refresh failed');
        }
      }

      if (requestConfig.headers) {
        requestConfig.headers.Authorization = `Bearer ${token}`;
      }
    }

    return requestConfig;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor — Fallback 401 Handling
 *
 * WHY BOTH REQUEST AND RESPONSE INTERCEPTORS:
 * The request interceptor handles the common case (token expiring soon).
 * But it can miss edge cases: server-side token revocation, clock skew
 * beyond the 5-minute buffer, or Cognito invalidating tokens after a
 * password change. The response interceptor catches these as a safety net.
 *
 * WHY _retry FLAG:
 * Without it, a 401 on the retried request would trigger infinite recursion:
 * 401 → refresh → retry → 401 → refresh → retry → ...
 * The _retry flag ensures we only attempt one refresh per original request.
 */
authInstance.interceptors.response.use(
  (response) => {
    // Track successful API response latency
    const startTime = (response.config as any)?._startTime;
    if (startTime) {
      const durationMs = Date.now() - startTime;
      const endpoint = (response.config.url || '').replace(config.API_URL, '').split('?')[0];
      Sentry.metrics.distribution('admin.api.response_time', durationMs, {
        unit: 'millisecond',
        attributes: { method: response.config.method || 'get', status: String(response.status), endpoint },
      });
    }
    Sentry.metrics.count('admin.api.request', 1, {
      attributes: { method: response.config.method || 'get', status: String(response.status) },
    });
    return response;
  },
  async (error: AxiosError) => {
    // Track failed API requests
    const status = error.response?.status || 0;
    const method = error.config?.method || 'get';
    Sentry.metrics.count('admin.api.request', 1, { attributes: { method, status: String(status) } });
    if (status >= 400) {
      Sentry.metrics.count('admin.api.error', 1, { attributes: { method, status: String(status) } });
    }
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized
    // IMPORTANT: Do NOT call clearTokens() or redirect to /login here.
    // Let React-Admin's checkAuth/checkError handle the logout flow.
    // Previously, background polling (ManagerAlerts, PermissionContext, etc.)
    // would trigger 401 → failed refresh → immediate clearTokens() + redirect,
    // logging users out even for transient failures (network blip, laptop wake).
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const token = getStoredToken();
      if (!token) {
        // No token — reject and let React-Admin's checkError redirect to login
        return Promise.reject(error);
      }

      // If already refreshing, wait via tokenRefreshService (single coordination point)
      if (isCurrentlyRefreshing()) {
        const freshToken = await waitForRefresh();
        if (freshToken) {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${freshToken}`;
          }
          return authInstance(originalRequest);
        }
        // Refresh failed — reject so React-Admin can handle it
        return Promise.reject(error);
      }

      try {
        Logger.info('[HttpClient] 401 received, attempting token refresh...');
        const newToken = await refreshToken();

        if (newToken) {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          return authInstance(originalRequest);
        }

        // Refresh failed — let React-Admin's checkError handle logout
        Logger.warn('[HttpClient] Token refresh failed on 401, deferring to checkError');
        return Promise.reject(error);
      } catch (refreshError) {
        Logger.warn('[HttpClient] Token refresh error, deferring to checkError:', refreshError);
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Fetch-compatible wrapper for React-Admin's data provider.
 *
 * WHY NOT USE authInstance DIRECTLY:
 * React-Admin's dataProvider expects a fetch-like interface (URL + RequestInit),
 * not Axios's (config object). This wrapper bridges the two APIs while still
 * routing token management through tokenRefreshService.getValidToken().
 *
 * WHY getValidToken() INSTEAD OF getStoredToken():
 * getValidToken() checks expiration and triggers a preemptive refresh if needed,
 * then returns a guaranteed-valid token. getStoredToken() just reads localStorage
 * and might return an expired token.
 */
export const createFetchClient = () => {
  return async (url: string, options: RequestInit = {}): Promise<{ json: any; headers: Headers; body: string; status: number }> => {
    // Use tokenRefreshService.getValidToken() — single source of truth for token management
    const token = await getValidToken();

    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...(options.headers as Record<string, string>),
    };

    // Only set Content-Type for non-FormData requests
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const fetchStart = Date.now();
    const response = await fetch(url, {
      ...options,
      headers,
    });

    const fetchDurationMs = Date.now() - fetchStart;
    const fetchEndpoint = url.replace(config.API_URL, '').split('?')[0];
    const fetchMethod = (options.method || 'GET').toUpperCase();
    Sentry.metrics.distribution('admin.api.response_time', fetchDurationMs, {
      unit: 'millisecond',
      attributes: { method: fetchMethod, status: String(response.status), endpoint: fetchEndpoint },
    });

    const text = await response.text();
    let json;
    try {
      json = JSON.parse(text);
    } catch {
      json = text;
    }

    if (response.status < 200 || response.status >= 300) {
      // Handle 401 - try refresh
      if (response.status === 401) {
        const newToken = await refreshToken();
        if (newToken) {
          // Retry the request with new token
          headers.Authorization = `Bearer ${newToken}`;
          const retryResponse = await fetch(url, {
            ...options,
            headers,
          });
          const retryText = await retryResponse.text();
          let retryJson;
          try {
            retryJson = JSON.parse(retryText);
          } catch {
            retryJson = retryText;
          }

          if (retryResponse.status >= 200 && retryResponse.status < 300) {
            // Copy headers to a new Headers object
            const responseHeaders = new Headers();
            retryResponse.headers.forEach((value, key) => {
              responseHeaders.set(key, value);
            });
            return {
              json: retryJson,
              headers: responseHeaders,
              body: retryText,
              status: retryResponse.status,
            };
          }
        }

        // Refresh failed or retry failed - let authProvider handle it
        const error = new Error(response.statusText) as Error & { status: number; body: any };
        error.status = response.status;
        error.body = json;
        throw error;
      }

      const error = new Error(response.statusText) as Error & { status: number; body: any };
      error.status = response.status;
      error.body = json;
      throw error;
    }

    // Copy headers to a new Headers object
    const responseHeaders = new Headers();
    response.headers.forEach((value, key) => {
      responseHeaders.set(key, value);
    });

    return {
      json,
      headers: responseHeaders,
      body: text,
      status: response.status,
    };
  };
};

export { authInstance, publicInstance };
export default authInstance;
